import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  GitBranch,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Star,
  Table2,
  Trash2,
  Workflow as WorkflowIcon,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { workflowApi } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import StatesEditor from '../components/workflows/StatesEditor';
import TransitionsEditor from '../components/workflows/TransitionsEditor';
import StatePropertiesModal from '../components/workflows/StatePropertiesModal';

// React Flow and dagre are only needed by the graph view. The bundle already
// warns on chunk size, so people who never open the canvas should not pay for
// it — this import is what keeps it out of the initial load.
const WorkflowGraph = lazy(() => import('../components/workflows/WorkflowGraph'));
import {
  ENTITY_TYPES,
  WORK_ITEM_TYPES,
  blankWorkflow,
  normalizeWorkflow,
  toPayload,
  unreachableStates,
  validateWorkflow,
  type Workflow,
} from '../components/workflows/workflowModel';

/**
 * Workflow author (F7) — the last story of the original DevOps plan.
 *
 * A full page rather than a slideout: states and transitions are tables, and the
 * 320px panel could not hold them (the same reason PI creation runs in a wizard).
 * It is the *only* editor for a workflow — the earlier read-only panel is gone,
 * because two editors for one object is what broke release creation in F3.
 */

type UsageInfo = Awaited<ReturnType<typeof workflowApi.usage>>;
type ViewMode = 'table' | 'graph';

const VIEW_KEY = 'workflowEditorView';

const INPUT =
  'px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-accent';

export default function WorkflowsPage() {
  const { hasRole } = useAuthStore();
  const readOnly = !hasRole('super_admin' as any);

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Workflow | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_KEY) as ViewMode) || 'table'
  );
  const [editingStateId, setEditingStateId] = useState<string | null>(null);
  const isNew = Boolean(draft && !draft._id);

  const chooseView = (next: ViewMode) => {
    localStorage.setItem(VIEW_KEY, next);
    setView(next);
  };

  const load = useCallback(async (keepId?: string) => {
    try {
      const rows = await workflowApi.list();
      const normalized = (rows || []).map(normalizeWorkflow);
      setWorkflows(normalized);
      const target = keepId || normalized[0]?._id || null;
      setSelectedId(target);
      const found = normalized.find((w) => w._id === target) || null;
      setDraft(found ? { ...found } : null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Usage follows the selection so the editor can show what an edit affects.
  useEffect(() => {
    if (!selectedId || isNew) { setUsage(null); return; }
    let alive = true;
    workflowApi
      .usage(selectedId)
      .then((u) => { if (alive) setUsage(u); })
      .catch(() => { if (alive) setUsage(null); });
    return () => { alive = false; };
  }, [selectedId, isNew]);

  const problems = useMemo(() => (draft ? validateWorkflow(draft) : []), [draft]);
  const unreachable = useMemo(() => (draft ? unreachableStates(draft) : []), [draft]);
  const stored = workflows.find((w) => w._id === selectedId);
  const dirty = useMemo(() => {
    if (!draft) return false;
    if (isNew) return true;
    return JSON.stringify(toPayload(draft)) !== JSON.stringify(stored ? toPayload(stored) : {});
  }, [draft, stored, isNew]);

  const select = (workflow: Workflow) => {
    if (dirty && !confirm('Discard unsaved changes?')) return;
    setSelectedId(workflow._id || null);
    setDraft({ ...workflow });
  };

  const startNew = () => {
    if (dirty && !confirm('Discard unsaved changes?')) return;
    setSelectedId(null);
    setUsage(null);
    setDraft(blankWorkflow());
  };

  const patch = (updates: Partial<Workflow>) =>
    setDraft((prev) => (prev ? { ...prev, ...updates } : prev));

  const save = async () => {
    if (!draft || problems.length) return;
    setSaving(true);
    try {
      const payload = toPayload(draft);
      const result = draft._id
        ? await workflowApi.update(draft._id, payload)
        : await workflowApi.create(payload);
      toast.success(draft._id ? 'Workflow saved' : 'Workflow created');
      await load(result._id);
    } catch (err: any) {
      // The server is the authority; surface exactly what it said. `errors`
      // carries marshmallow field errors, `error` a structural refusal.
      const detail = err.detail?.errors
        ? JSON.stringify(err.detail.errors)
        : err.message;
      toast.error(detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft?._id) return;
    if (!confirm(`Delete the workflow "${draft.name}"? This cannot be undone.`)) return;
    try {
      await workflowApi.delete(draft._id);
      toast.success('Workflow deleted');
      await load();
    } catch (err: any) {
      // 409 when projects still bind to it, or it is the default.
      toast.error(err.message || 'Delete failed');
    }
  };

  const seed = async () => {
    try {
      const result = await workflowApi.seed();
      toast.success(`Seeded ${Object.keys(result || {}).length} workflow(s)`);
      await load(selectedId || undefined);
    } catch (err: any) {
      toast.error(err.message || 'Seeding failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* ---- Workflow list ---- */}
      <aside className="w-64 shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-3 h-14 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Workflows</h2>
          {!readOnly && (
            <button
              onClick={startNew}
              className="p-1 rounded text-accent hover:bg-accent/10"
              title="New workflow"
            >
              <Plus size={16} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {workflows.map((workflow) => (
            <button
              key={workflow._id}
              onClick={() => select(workflow)}
              className={clsx(
                'w-full flex items-start gap-2 px-2 py-2 rounded-lg text-left',
                selectedId === workflow._id && !isNew
                  ? 'bg-accent/10 text-accent'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              <GitBranch size={14} className="shrink-0 mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium truncate">{workflow.name}</span>
                <span className="block text-[10px] text-gray-500 dark:text-gray-400">
                  {workflow.entityType} · {workflow.states.length} states · v{workflow.version || 1}
                  {!workflow.active && ' · inactive'}
                </span>
              </span>
              {workflow.isDefault && (
                <span title="Default for this entity type" className="shrink-0 mt-0.5">
                  <Star size={11} className="text-amber-500" />
                </span>
              )}
            </button>
          ))}

          {isNew && (
            <div className="w-full flex items-center gap-2 px-2 py-2 rounded-lg bg-accent/10 text-accent">
              <Sparkles size={14} className="shrink-0" />
              <span className="text-[13px] font-medium truncate">{draft?.name || 'New workflow'}</span>
            </div>
          )}

          {workflows.length === 0 && !isNew && (
            <p className="text-xs text-gray-500 dark:text-gray-400 px-2 py-3">
              No workflows yet.
            </p>
          )}
        </div>

        {!readOnly && (
          <div className="p-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={seed}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent hover:text-accent"
              title="Create or refresh the built-in Default and release lifecycle"
            >
              <RotateCcw size={12} /> Seed defaults
            </button>
          </div>
        )}
      </aside>

      {/* ---- Editor ---- */}
      <div className="flex-1 overflow-y-auto">
        {!draft ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <GitBranch size={28} className="text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              No workflow selected.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm">
              A workflow defines the columns an item can be in and which moves between
              them are legal. Pick one on the left, or seed the defaults.
            </p>
          </div>
        ) : (
          <div className="p-5 max-w-5xl space-y-6">
            {/* Header + save */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {draft.name || 'Untitled workflow'}
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {isNew
                    ? 'Not saved yet'
                    : `Version ${draft.version || 1} · a structural change bumps the version; items keep the version they were created under`}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Table and graph are two projections over the same draft, so
                    switching costs nothing and loses no edits. */}
                <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  {([['table', 'Table', Table2], ['graph', 'Graph', WorkflowIcon]] as const).map(
                    ([mode, label, Icon]) => (
                      <button
                        key={mode}
                        onClick={() => chooseView(mode)}
                        aria-pressed={view === mode}
                        className={clsx(
                          'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium',
                          view === mode
                            ? 'bg-accent text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        )}
                      >
                        <Icon size={13} /> {label}
                      </button>
                    )
                  )}
                </div>
                {!readOnly && (
                  <>
                  {!isNew && (
                    <button
                      onClick={remove}
                      disabled={usage ? !usage.canDelete : false}
                      title={usage?.blockedReason || 'Delete this workflow'}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-red-400 hover:text-red-500 disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-600"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                  <button
                    onClick={save}
                    disabled={saving || problems.length > 0 || !dirty}
                    title={
                      problems.length
                        ? 'Fix the problems listed below first'
                        : !dirty
                          ? 'No changes to save'
                          : undefined
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-40"
                  >
                    <Save size={14} />
                    {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
                  </button>
                  </>
                )}
              </div>
            </div>

            {readOnly && (
              <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-750">
                You have read access to workflows. Editing requires super admin.
              </p>
            )}

            {/* Validation */}
            {problems.length > 0 && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <AlertTriangle size={14} className="text-amber-600 dark:text-amber-500" />
                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                    The API will refuse this definition
                  </span>
                </div>
                <ul className="text-xs text-amber-800 dark:text-amber-300 space-y-0.5 ml-5 list-disc">
                  {problems.map((problem, i) => (
                    <li key={i}>{problem.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Definition */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Definition</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    value={draft.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    disabled={readOnly}
                    className={`${INPUT} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Governs
                  </label>
                  <select
                    value={draft.entityType}
                    onChange={(e) => patch({ entityType: e.target.value })}
                    disabled={readOnly}
                    className={`${INPUT} w-full`}
                  >
                    {ENTITY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <input
                  value={draft.description}
                  onChange={(e) => patch({ description: e.target.value })}
                  disabled={readOnly}
                  placeholder="What this workflow is for"
                  className={`${INPUT} w-full`}
                />
              </div>

              {draft.entityType === 'workitem' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Applies to work item types
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                      — none selected means any type no more specific workflow claims
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {WORK_ITEM_TYPES.map((type) => (
                      <button
                        key={type}
                        disabled={readOnly}
                        onClick={() =>
                          patch({
                            appliesTo: draft.appliesTo.includes(type)
                              ? draft.appliesTo.filter((t) => t !== type)
                              : [...draft.appliesTo, type],
                          })
                        }
                        className={clsx(
                          'px-2 py-0.5 rounded-full border text-[11px] disabled:opacity-50',
                          draft.appliesTo.includes(type)
                            ? 'bg-accent text-white border-accent'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent'
                        )}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-5">
                <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.active}
                    onChange={(e) => patch({ active: e.target.checked })}
                    disabled={readOnly}
                    className="rounded border-gray-300 text-accent focus:ring-accent"
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.isDefault}
                    onChange={(e) => patch({ isDefault: e.target.checked })}
                    disabled={readOnly}
                    className="rounded border-gray-300 text-accent focus:ring-accent"
                  />
                  Default for {draft.entityType}
                  <span className="text-gray-500 dark:text-gray-400">
                    — replaces any current default
                  </span>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.forbidForce}
                    onChange={(e) => patch({ forbidForce: e.target.checked })}
                    disabled={readOnly}
                    className="rounded border-gray-300 text-accent focus:ring-accent"
                  />
                  Forbid forced moves
                  <span className="text-gray-500 dark:text-gray-400">
                    — admins cannot skip this workflow's gates
                  </span>
                </label>
                <label className="flex flex-col gap-1 text-xs text-gray-700 dark:text-gray-300">
                  Server-managed custom fields
                  <input
                    value={draft.lockedFields.join(', ')}
                    onChange={(e) => patch({
                      lockedFields: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })}
                    disabled={readOnly}
                    placeholder="e.g. spendAuthorized, gateInputs"
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                  />
                  <span className="text-gray-500 dark:text-gray-400">
                    Comma-separated. Only the server may set these on items this workflow governs.
                  </span>
                </label>
              </div>
            </section>

            {view === 'graph' ? (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-[32rem] rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
                  </div>
                }
              >
                <WorkflowGraph
                  workflow={draft}
                  stateCounts={usage?.stateCounts || {}}
                  unreachable={unreachable}
                  readOnly={readOnly}
                  onStatesChange={(states) => patch({ states })}
                  onTransitionsChange={(transitions) => patch({ transitions })}
                  onSelectState={setEditingStateId}
                  onSelectTransition={() => chooseView('table')}
                />
              </Suspense>
            ) : (
              <StatesEditor
                workflow={draft}
                stateCounts={usage?.stateCounts || {}}
                unreachable={unreachable}
                readOnly={readOnly}
                onChange={(states) => patch({ states })}
              />
            )}

            <TransitionsEditor
              workflow={draft}
              onChange={(transitions) => patch({ transitions })}
              readOnly={readOnly}
            />

            {/* Node properties, opened from the canvas */}
            {editingStateId && draft.states.some((s) => s.id === editingStateId) && (
              <StatePropertiesModal
                state={draft.states.find((s) => s.id === editingStateId)!}
                takenIds={draft.states
                  .filter((s) => s.id !== editingStateId)
                  .map((s) => s.id)}
                itemCount={usage?.stateCounts?.[editingStateId] || 0}
                readOnly={readOnly}
                onClose={() => setEditingStateId(null)}
                onSave={(updated) => {
                  patch({
                    states: draft.states.map((s) => {
                      if (s.id !== editingStateId) {
                        // Only one state may be initial, so setting it here
                        // clears whichever held it before.
                        return updated.isInitial ? { ...s, isInitial: false } : s;
                      }
                      return updated;
                    }),
                    // A rename has to carry through the transitions that point
                    // at it, or saving would fail structural validation.
                    transitions:
                      updated.id === editingStateId
                        ? draft.transitions
                        : draft.transitions.map((t) => ({
                            ...t,
                            from: t.from.map((f) => (f === editingStateId ? updated.id : f)),
                            to: t.to === editingStateId ? updated.id : t.to,
                          })),
                  });
                  setEditingStateId(null);
                }}
                onDelete={() => {
                  const held = usage?.stateCounts?.[editingStateId] || 0;
                  const refs = draft.transitions.filter(
                    (t) => t.to === editingStateId || t.from.includes(editingStateId)
                  ).length;
                  const detail = [
                    held ? `${held} item${held === 1 ? '' : 's'} sit in it` : '',
                    refs ? `${refs} transition${refs === 1 ? '' : 's'} reference it` : '',
                  ].filter(Boolean).join(', and ');
                  if (!confirm(`Remove this state?${detail ? `

${detail}.` : ''}`)) return;
                  patch({
                    states: draft.states
                      .filter((s) => s.id !== editingStateId)
                      .map((s, i) => ({ ...s, order: i })),
                  });
                  setEditingStateId(null);
                }}
              />
            )}

            {/* Usage */}
            {usage && (
              <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Where this is used
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {usage.entityCount} {usage.entityType}
                  {usage.entityCount === 1 ? '' : 's'} created under this workflow.
                </p>

                {usage.boundProjects.length > 0 ? (
                  <ul className="mt-2 space-y-0.5">
                    {usage.boundProjects.map((project) => (
                      <li key={project.id} className="text-xs text-gray-700 dark:text-gray-300">
                        <span className="font-medium">{project.name}</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {project.isProjectDefault ? ' — project default' : ''}
                          {project.boundTypes.length ? ` — bound for ${project.boundTypes.join(', ')}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    No project binds to it directly.
                  </p>
                )}

                {usage.orphanedStates.length > 0 && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
                    <AlertTriangle size={12} className="inline mr-1" />
                    Items are sitting in {usage.orphanedStates.join(', ')}, which this
                    workflow no longer defines. They keep the version they were created
                    under, so they still work — but they will not appear on a board built
                    from the current states.
                  </p>
                )}

                {!usage.canDelete && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Cannot be deleted: {usage.blockedReason}
                  </p>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
